/**
 * WorkflowBuilderWorkspace.js
 *
 * Visual workflow/wizard designer for FAST Studio.
 * Allows creating and editing guided workflows with drag-drop step ordering,
 * field configuration, and live testing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  ContentCopy as CopyIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  AccountTree as WorkflowIcon,
  LinearScale as StepIcon,
  TextFields as FieldIcon,
  Code as ScriptIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme } from '../../../contexts/ThemeContext';
import WorkflowService from '../../../services/WorkflowService';
import AgentFlowCanvas from './flow/AgentFlowCanvas';

// Step type configurations
const STEP_TYPES = {
  ORIENTATION: { label: 'Orientation', color: '#9c27b0', description: 'Welcome/intro step with context' },
  GUIDED: { label: 'Guided Input', color: '#2196f3', description: 'Collect user input via form fields' },
  IDENTIFY_ENTITY: { label: 'Identify Entity', color: '#00bcd4', description: 'Select or search for an entity' },
  SYSTEM_ASSUMPTION: { label: 'System Assumption', color: '#ff9800', description: 'Show system defaults with override option' },
  REQUIRED_INPUT: { label: 'Required Input', color: '#f44336', description: 'Mandatory field input' },
  REVIEW: { label: 'Review', color: '#4caf50', description: 'Summary before execution' },
  CONFIRMATION: { label: 'Confirmation', color: '#8bc34a', description: 'Final approval step' },
  EXECUTION: { label: 'Execution', color: '#673ab7', description: 'Execute action and show results' },
  COMPLETION: { label: 'Completion', color: '#009688', description: 'Success with next actions' },
};

// Field type configurations
const FIELD_TYPES = {
  TEXT: { label: 'Text', icon: 'Aa' },
  NUMBER: { label: 'Number', icon: '#' },
  DATE: { label: 'Date', icon: '📅' },
  SELECT: { label: 'Select', icon: '▼' },
  COMBOBOX: { label: 'Combobox', icon: '⬇' },
  CHECKBOX: { label: 'Checkbox', icon: '☑' },
  TEXTAREA: { label: 'Text Area', icon: '📝' },
};

const WorkflowBuilderWorkspace = ({ colors }) => {
  const { isDarkMode } = useTheme();

  // State
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: List, 1: Editor, 2: Test
  const [editorTab, setEditorTab] = useState(0); // 0: Steps, 1: Settings, 2: Script
  const [steps, setSteps] = useState([]);
  const [expandedStep, setExpandedStep] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit'
  const [canvasMode, setCanvasMode] = useState(false); // false: linear editor, true: visual canvas
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: 'WMS',
    triggers: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);

  // Load workflows
  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await WorkflowService.getTools();
      // Filter for WIZARD category tools (parent workflows only)
      const wizards = response.filter(t =>
        t.category === 'WIZARD' && !t.parentToolId
      );
      setWorkflows(wizards);
    } catch (err) {
      console.error('Failed to load workflows:', err);
      setError('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load workflow steps
  const loadWorkflowSteps = useCallback(async (workflowId) => {
    try {
      const workflowSteps = await WorkflowService.getWizardSteps(workflowId);
      setSteps(workflowSteps);
    } catch (err) {
      console.error('Failed to load steps:', err);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    if (selectedWorkflow) {
      loadWorkflowSteps(selectedWorkflow.toolId);
    }
  }, [selectedWorkflow, loadWorkflowSteps]);

  // Filter workflows by search
  const filteredWorkflows = workflows.filter(w =>
    w.toolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.toolKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle workflow selection
  const handleSelectWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setActiveTab(1); // Switch to editor
    setEditorTab(0); // Start with steps tab
  };

  // Handle create new workflow
  const handleCreateNew = () => {
    setDialogMode('create');
    setFormData({
      name: '',
      description: '',
      domain: 'WMS',
      triggers: '',
    });
    setDialogOpen(true);
  };

  // Handle edit workflow settings
  const handleEditSettings = () => {
    if (!selectedWorkflow) return;
    setDialogMode('edit');

    // Parse triggers from capabilities
    let triggers = '';
    try {
      const caps = selectedWorkflow.capabilities ||
        (typeof selectedWorkflow.capabilitiesJson === 'string' ?
          JSON.parse(selectedWorkflow.capabilitiesJson) : selectedWorkflow.capabilitiesJson) ||
        {};
      triggers = (caps.triggers || []).join(', ');
    } catch (e) {}

    setFormData({
      name: selectedWorkflow.toolName || '',
      description: selectedWorkflow.description || '',
      domain: selectedWorkflow.domain || 'WMS',
      triggers: triggers,
    });
    setDialogOpen(true);
  };

  // Handle save workflow
  const handleSaveWorkflow = async () => {
    setSaving(true);
    try {
      const triggers = formData.triggers.split(',').map(t => t.trim()).filter(Boolean);

      const toolData = {
        toolName: formData.name,
        toolKey: formData.name.toLowerCase().replace(/\s+/g, '_') + '_wizard',
        description: formData.description,
        domain: formData.domain,
        category: 'WIZARD',
        capabilitiesJson: JSON.stringify({
          triggers,
          timeout_minutes: 30,
          allow_save_draft: false,
          execution_mode: 'sequential',
        }),
        active: true,
      };

      if (dialogMode === 'edit' && selectedWorkflow) {
        await WorkflowService.updateTool(selectedWorkflow.toolId, toolData);
        // Refresh the selected workflow
        const updated = { ...selectedWorkflow, ...toolData };
        setSelectedWorkflow(updated);
      } else {
        const created = await WorkflowService.createTool(toolData);
        setSelectedWorkflow(created);
        setActiveTab(1);
      }

      await loadWorkflows();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save workflow:', err);
      setError('Failed to save workflow: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle step reorder
  const handleMoveStep = async (index, direction) => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= steps.length) return;

    // Swap steps
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];

    // Update sequence orders
    newSteps.forEach((step, i) => {
      step.sequenceOrder = i + 1;
    });

    setSteps(newSteps);

    // Save to backend using the reorder endpoint
    try {
      const stepIds = newSteps.map(s => s.toolId);
      await WorkflowService.reorderWizardSteps(selectedWorkflow.toolId, stepIds);
    } catch (err) {
      console.error('Failed to reorder steps:', err);
    }
  };

  // Parse step type from capabilities
  const getStepType = (step) => {
    try {
      // capabilities may already be parsed by the backend
      const caps = step.capabilities ||
        (typeof step.capabilitiesJson === 'string' ? JSON.parse(step.capabilitiesJson) : step.capabilitiesJson) ||
        {};
      return caps.step_type || 'GUIDED';
    } catch (e) {
      return 'GUIDED';
    }
  };

  // Parse step number from capabilities
  const getStepNumber = (step) => {
    try {
      const caps = step.capabilities ||
        (typeof step.capabilitiesJson === 'string' ? JSON.parse(step.capabilitiesJson) : step.capabilitiesJson) ||
        {};
      return caps.step_number || step.sequenceOrder || 1;
    } catch (e) {
      return step.sequenceOrder || 1;
    }
  };

  // Render workflow list
  const renderWorkflowList = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search workflows..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textSecondary }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCanvasMode(true)}
          sx={{ bgcolor: colors.accent, textTransform: 'none' }}
        >
          New Workflow
        </Button>
        <IconButton onClick={loadWorkflows} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textSecondary, fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textSecondary, fontWeight: 600 }}>Key</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textSecondary, fontWeight: 600 }}>Domain</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textSecondary, fontWeight: 600 }}>Steps</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textSecondary, fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textSecondary, fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredWorkflows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: colors.textSecondary }}>
                  No workflows found
                </TableCell>
              </TableRow>
            ) : (
              filteredWorkflows.map((workflow) => (
                <TableRow
                  key={workflow.toolId}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: colors.hover } }}
                  onClick={() => { handleSelectWorkflow(workflow); setCanvasMode(true); }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkflowIcon sx={{ color: colors.accent, fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={500}>{workflow.toolName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: colors.textSecondary }}>
                      {workflow.toolKey}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={workflow.domain || 'WMS'} size="small" sx={{ bgcolor: colors.surface }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{workflow.stepCount || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={workflow.active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: workflow.active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)',
                        color: workflow.active ? '#4caf50' : colors.textSecondary,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectWorkflow(workflow); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Duplicate">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Test">
                      <PlayIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Render step editor
  const renderStepEditor = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Workflow Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => { setSelectedWorkflow(null); setActiveTab(0); }}>
          <CloseIcon />
        </IconButton>
        <WorkflowIcon sx={{ color: colors.accent }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{selectedWorkflow?.toolName}</Typography>
          <Typography variant="caption" color="textSecondary">
            {selectedWorkflow?.toolKey} • {steps.length} steps
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={handleEditSettings}>
          Settings
        </Button>
        <Button variant="outlined" size="small" startIcon={<PlayIcon />}>
          Test
        </Button>
        <Button variant="contained" size="small" startIcon={<SaveIcon />} sx={{ bgcolor: colors.accent }}>
          Save All
        </Button>
      </Box>

      {/* Editor Tabs */}
      <Box sx={{ borderBottom: `1px solid ${colors.border}` }}>
        <Tabs value={editorTab} onChange={(e, v) => setEditorTab(v)}>
          <Tab label="Steps" icon={<StepIcon />} iconPosition="start" />
          <Tab label="Fields" icon={<FieldIcon />} iconPosition="start" />
          <Tab label="Script" icon={<ScriptIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {editorTab === 0 && (
          <Box>
            {/* Add Step Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mb: 2 }}
              onClick={() => {/* TODO: Add step dialog */}}
            >
              Add Step
            </Button>

            {/* Steps List */}
            <List>
              {steps.map((step, index) => {
                const stepType = getStepType(step);
                const stepConfig = STEP_TYPES[stepType] || STEP_TYPES.GUIDED;

                return (
                  <Card
                    key={step.toolId}
                    sx={{
                      mb: 1,
                      borderLeft: `4px solid ${stepConfig.color}`,
                      bgcolor: colors.surface,
                    }}
                  >
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DragIcon sx={{ color: colors.textSecondary, cursor: 'grab' }} />
                        <Chip
                          label={`Step ${getStepNumber(step)}`}
                          size="small"
                          sx={{ bgcolor: stepConfig.color, color: 'white', fontWeight: 600 }}
                        />
                        <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                          {step.toolName}
                        </Typography>
                        <Chip label={stepConfig.label} size="small" variant="outlined" />
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => handleMoveStep(index, 'up')}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={index === steps.length - 1}
                          onClick={() => handleMoveStep(index, 'down')}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedStep(expandedStep === step.toolId ? null : step.toolId)}
                        >
                          {expandedStep === step.toolId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      <Collapse in={expandedStep === step.toolId}>
                        <Box sx={{ mt: 2, pl: 4 }}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {step.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: colors.textSecondary }}>
                            Key: {step.toolKey}
                          </Typography>

                          {/* Step Configuration Preview */}
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Configuration</Typography>
                            <TextField
                              multiline
                              fullWidth
                              minRows={4}
                              maxRows={10}
                              value={JSON.stringify(step.capabilities || {}, null, 2)}
                              InputProps={{
                                readOnly: true,
                                sx: { fontFamily: 'monospace', fontSize: 12 }
                              }}
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </List>

            {steps.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: colors.textSecondary }}>
                <StepIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography>No steps defined yet</Typography>
                <Typography variant="body2">Click "Add Step" to create your first step</Typography>
              </Box>
            )}
          </Box>
        )}

        {editorTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Field Configuration</Typography>
            <Typography variant="body2" color="textSecondary">
              Configure fields used across all steps in this workflow.
            </Typography>
            {/* TODO: Field editor */}
          </Box>
        )}

        {editorTab === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Execution Script</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              FLEX script executed when the workflow completes.
            </Typography>
            <TextField
              multiline
              fullWidth
              minRows={12}
              value={selectedWorkflow?.flexScriptName || 'No script configured'}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: 13 }
              }}
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  // Render create/edit dialog
  const renderDialog = () => (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {dialogMode === 'create' ? 'Create New Workflow' : 'Edit Workflow Settings'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Workflow Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
            placeholder="e.g., Quick Receive"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
            placeholder="Describe what this workflow does"
          />
          <FormControl fullWidth>
            <InputLabel>Domain</InputLabel>
            <Select
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              label="Domain"
            >
              <MenuItem value="WMS">WMS</MenuItem>
              <MenuItem value="INVENTORY">Inventory</MenuItem>
              <MenuItem value="RECEIVING">Receiving</MenuItem>
              <MenuItem value="SHIPPING">Shipping</MenuItem>
              <MenuItem value="GENERAL">General</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Trigger Phrases"
            value={formData.triggers}
            onChange={(e) => setFormData({ ...formData, triggers: e.target.value })}
            fullWidth
            placeholder="receive, receiving, quick receive"
            helperText="Comma-separated phrases that trigger this workflow"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSaveWorkflow}
          disabled={!formData.name || saving}
          sx={{ bgcolor: colors.accent }}
        >
          {saving ? <CircularProgress size={20} /> : (dialogMode === 'create' ? 'Create' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.background,
        color: colors.text,
        position: 'relative',
      }}
    >
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      {canvasMode ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Canvas header with back button */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.5,
            borderBottom: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
          }}>
            <IconButton size="small" onClick={() => setCanvasMode(false)} sx={{ color: colors.textSecondary }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <WorkflowIcon sx={{ color: colors.accent, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: colors.text, fontWeight: 600 }}>
              Workflow Designer
            </Typography>
          </Box>
          <AgentFlowCanvas
            colors={colors}
            onSave={(nodes, edges) => {
              console.log('Workflow saved:', { nodes, edges });
              // TODO: persist to backend
            }}
          />
        </Box>
      ) : (
        renderWorkflowList()
      )}

      {/* Dialogs */}
      {renderDialog()}
    </Box>
  );
};

export default WorkflowBuilderWorkspace;
